import React, { useEffect, useState } from "react";
import { Loader2, User, Car, Store, PackageCheck, MapPin, Phone, Mail, Receipt, Wallet, Image as ImageIcon, ShieldCheck, Camera, Star, History } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { adminApi, errorMessage, fmtDateTime, titleCase, inr } from "../../lib/api";
import { TrustBadge } from "../app/TipAndTrust";
import { toast } from "sonner";

const KIND_LABEL = { ride: "Taxi ride", goods: "Goods delivery", food: "Food order", private_car: "Private driver" };

function money(n) {
  if (n == null || n === "") return "—";
  return inr(Number(n));
}

function PersonCard({ title, icon: Icon, person, extra }) {
  if (!person && !extra) return null;
  return (
    <div className="kt-card p-4">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted"><Icon className="h-3.5 w-3.5" /> {title}</p>
      {person ? (
        <>
          <p className="font-semibold text-ink">{person.name || "—"}</p>
          {person.vehicle && <p className="text-sm text-ink-soft">{person.vehicle}{person.vehicle_plate ? ` · ${person.vehicle_plate}` : ""}</p>}
          {!person.vehicle && person.vehicle_plate && <p className="text-sm text-ink-soft">{person.vehicle_plate}</p>}
          {person.rating != null && <p className="text-sm text-ink-soft">★ {Number(person.rating).toFixed(1)}</p>}
          <div className="mt-1.5 space-y-0.5 text-sm">
            {person.phone && <a href={`tel:${person.phone}`} className="flex items-center gap-1.5 text-ink-soft hover:text-brand"><Phone className="h-3.5 w-3.5" /> {person.phone}</a>}
            {person.email && <a href={`mailto:${person.email}`} className="flex items-center gap-1.5 text-ink-soft hover:text-brand"><Mail className="h-3.5 w-3.5" /> {person.email}</a>}
          </div>
        </>
      ) : (
        <p className="text-sm text-ink-muted">Not assigned yet</p>
      )}
      {extra}
    </div>
  );
}

function Line({ label, value, strong, tone }) {
  if (value == null || value === "" || value === 0) return null;
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className="text-ink-soft">{label}</span>
      <span className={`${strong ? "font-bold text-ink" : "font-medium text-ink"} ${tone === "green" ? "text-emerald-700" : tone === "red" ? "text-red-600" : ""}`}>{value}</span>
    </div>
  );
}

function ProofImage({ rideId, photoId, label }) {
  if (!photoId) return null;
  const url = adminApi.ridePhotoUrl(rideId, photoId);
  return (
    <a href={url} target="_blank" rel="noreferrer" className="block" data-testid={`order-proof-${label}`}>
      <p className="mb-1 text-xs font-semibold text-ink-muted">{label}</p>
      <img src={url} alt={label} className="h-28 w-full rounded-lg border border-surface-200 object-cover" />
    </a>
  );
}

function ProofUrlImage({ url, label }) {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="block" data-testid={`order-proof-${label}`}>
      <p className="mb-1 text-xs font-semibold text-ink-muted">{label}</p>
      <img src={url} alt={label} className="h-28 w-full rounded-lg border border-surface-200 object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
    </a>
  );
}

export default function AdminOrderDetailDialog({ order, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!order) { setDetail(null); return; }
    let alive = true;
    setLoading(true);
    adminApi.orderDetail(order.kind, order.id)
      .then((d) => { if (alive) setDetail(d); })
      .catch((e) => { if (alive) { toast.error(errorMessage(e)); onClose?.(); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [order]); // eslint-disable-line react-hooks/exhaustive-deps

  const d = detail;
  const inv = d?.invoice || {};
  const com = d?.commission || {};
  const isRideLike = d && (d.kind === "ride" || d.kind === "goods");
  const isFood = d?.kind === "food";
  const isPc = d?.kind === "private_car";

  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="admin-order-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>{d ? KIND_LABEL[d.kind] || "Order" : "Order"} detail</span>
            {d?.order_number && <span className="rounded-full bg-brand/10 px-2 py-0.5 font-mono text-xs font-semibold text-brand" data-testid="order-detail-number">{d.order_number}</span>}
          </DialogTitle>
          <DialogDescription>Full operational, invoice and commission detail (admin view).</DialogDescription>
        </DialogHeader>

        {loading || !d ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : (
          <div className="space-y-4" data-testid="admin-order-detail-body">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="rounded-full bg-surface-200 px-2.5 py-0.5 font-semibold text-ink-soft">{titleCase(d.status || "")}</span>
              {d.vehicle_type && <span className="text-ink-muted">{titleCase(d.vehicle_type)}</span>}
              {d.kind === "goods" && d.handoff?.drop_code_verified ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700" data-testid="admin-handoff-verified"><ShieldCheck className="h-3.5 w-3.5" /> Code-verified handoff</span>
              ) : d.kind === "goods" && d.handoff?.completed_without_code ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-surface-200 px-2.5 py-0.5 text-xs font-semibold text-ink-soft" data-testid="admin-handoff-photo"><Camera className="h-3.5 w-3.5" /> Completed with photo proof</span>
              ) : null}
              {d.kind === "goods" && d.partner_trust?.eligible ? <TrustBadge trust={d.partner_trust} /> : null}
              <span className="text-ink-muted">· {fmtDateTime(d.created_at)}</span>
            </div>
            {d.cancelled_reason && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{d.cancelled_reason}</p>}

            <div className="grid gap-3 sm:grid-cols-2">
              <PersonCard title="Customer" icon={User} person={d.customer} />
              {(isRideLike || isPc) && <PersonCard title="Driver" icon={Car} person={d.driver} />}
              {isFood && <PersonCard title="Delivery partner" icon={Car} person={d.driver} />}
              {isFood && d.restaurant && (
                <PersonCard title="Restaurant" icon={Store} person={d.restaurant.owner} extra={
                  <div className="mt-1 text-sm">
                    <p className="font-semibold text-ink">{d.restaurant.name}</p>
                    {d.restaurant.address && <p className="text-ink-soft">{d.restaurant.address}</p>}
                    {d.restaurant.phone && <a href={`tel:${d.restaurant.phone}`} className="flex items-center gap-1.5 text-ink-soft hover:text-brand"><Phone className="h-3.5 w-3.5" /> {d.restaurant.phone}</a>}
                  </div>
                } />
              )}
              {d.kind === "goods" && d.receiver && (
                <PersonCard title="Receiver" icon={PackageCheck} person={null} extra={
                  <div className="text-sm">
                    <p className="font-semibold text-ink">{d.receiver.name || "—"}</p>
                    {d.receiver.phone && <a href={`tel:${d.receiver.phone}`} className="flex items-center gap-1.5 text-ink-soft hover:text-brand"><Phone className="h-3.5 w-3.5" /> {d.receiver.phone}</a>}
                    {d.package?.weight_kg != null && <p className="text-ink-soft">Weight: {d.package.weight_kg} kg</p>}
                    {d.package?.instructions && <p className="text-ink-muted">“{d.package.instructions}”</p>}
                  </div>
                } />
              )}
            </div>

            {/* Route / addresses */}
            <div className="kt-card p-4">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted"><MapPin className="h-3.5 w-3.5" /> {isFood ? "Delivery" : "Route"}</p>
              {d.pickup?.address && <Line label="Pickup" value={d.pickup.address} />}
              {Array.isArray(d.stops) && d.stops.map((s, i) => <Line key={i} label={`Stop ${i + 1}`} value={s.address} />)}
              {d.drop?.address && <Line label={isPc ? "Destination" : "Drop"} value={d.drop.address} />}
              {d.delivery?.address && <Line label="Deliver to" value={d.delivery.address} />}
              {d.delivery?.note && <Line label="Note" value={d.delivery.note} />}
              {d.days && <Line label="Days" value={d.days} />}
              {inv.distance_km != null && <Line label="Distance" value={`${inv.distance_km} km`} />}
              {inv.duration_min != null && <Line label="Duration" value={`${inv.duration_min} min`} />}
            </div>

            {/* Food items */}
            {isFood && Array.isArray(d.items) && d.items.length > 0 && (
              <div className="kt-card p-4" data-testid="order-detail-items">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">Items</p>
                {d.items.map((it, i) => (
                  <div key={i} className="flex justify-between py-0.5 text-sm">
                    <span className="text-ink-soft">{it.qty}× {it.name}</span>
                    <span className="font-medium text-ink">{money(it.line_total)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Invoice */}
            <div className="kt-card p-4" data-testid="order-detail-invoice">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted"><Receipt className="h-3.5 w-3.5" /> Invoice</p>
              {isFood ? (
                <>
                  <Line label="Subtotal" value={money(inv.subtotal)} />
                  <Line label="Packaging" value={money(inv.packaging_fee)} />
                  <Line label="GST" value={money(inv.tax)} />
                  <Line label="Delivery fee" value={money(inv.delivery_fee)} />
                  <Line label="Tip" value={money(inv.tip)} />
                  <Line label="Discount" value={inv.discount ? `− ${money(inv.discount)}` : null} tone="green" />
                </>
              ) : isPc ? (
                <>
                  <Line label="Driver fare" value={money(inv.driver_fare)} />
                  <Line label="Platform fee (base)" value={money(inv.platform_fee_base)} />
                  <Line label="Platform fee GST" value={money(inv.platform_fee_gst)} />
                </>
              ) : (
                <>
                  <Line label="Base fare" value={money(inv.base_fare)} />
                  <Line label="Distance charge" value={money(inv.distance_charge)} />
                  <Line label="Time charge" value={money(inv.time_charge)} />
                  {inv.surge_amount ? <Line label={`Surge ×${inv.surge_multiplier}`} value={money(inv.surge_amount)} /> : null}
                  <Line label="Waiting charge" value={money(inv.waiting_charge)} />
                  <Line label="Extra distance" value={money(inv.distance_fee)} />
                  <Line label="Carried cancel fee" value={money(inv.applied_cancel_balance)} />
                  <Line label="Discount" value={inv.discount ? `− ${money(inv.discount)}` : null} tone="green" />
                </>
              )}
              <div className="mt-1 border-t border-surface-200 pt-1">
                <Line label="Total bill" value={money(inv.total_bill)} strong />
              </div>
              {inv.reconcile_note && <p className="mt-1 text-xs text-amber-700">{inv.reconcile_note}</p>}
            </div>

            {/* Commission / earnings */}
            <div className="kt-card p-4" data-testid="order-detail-commission">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted"><Wallet className="h-3.5 w-3.5" /> Commission & earnings</p>
              <Line label="Gross" value={money(com.gross)} />
              {isFood ? (
                <>
                  <Line label={com.commission_label} value={money(com.restaurant_commission)} tone="green" />
                  <Line label="Packaging collected" value={money(com.packaging)} />
                  <Line label="GST collected" value={money(com.gst_collected)} />
                  <Line label="Penalty" value={com.penalty ? money(com.penalty) : null} tone="red" />
                  <Line label={com.net_label} value={money(com.net_to_restaurant)} strong />
                  <Line label={com.driver_earning_label} value={money(com.driver_earning)} />
                </>
              ) : (
                <>
                  <Line label={com.commission_label} value={money(com.platform_commission)} tone="green" />
                  <Line label={com.net_label} value={money(com.net_to_partner)} strong />
                </>
              )}
            </div>

            {/* Payment */}
            <div className="kt-card p-4" data-testid="order-detail-payment">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">Payment</p>
              <Line label="Method" value={d.payment?.method === "razorpay" ? "Online (Razorpay)" : titleCase(d.payment?.method || "")} />
              <Line label="Status" value={titleCase(d.payment?.status || "")} />
              <Line label="Amount paid" value={d.payment?.amount_paid != null ? money(d.payment.amount_paid) : null} />
              {d.payment?.cod_payer && <Line label="COD payer" value={titleCase(d.payment.cod_payer)} />}
              {d.payment?.cod_amount ? <Line label="Collect from receiver" value={money(d.payment.cod_amount)} tone="red" /> : null}
            </div>

            {/* Goods proof */}
            {d.kind === "goods" && d.proof && (d.proof.package_photo_url || d.proof.pickup_photo_id || d.proof.proof_photo_id || d.proof.proof_signature_id) && (
              <div className="kt-card p-4" data-testid="order-detail-proof">
                <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted"><ImageIcon className="h-3.5 w-3.5" /> Package &amp; delivery proof</p>
                {d.proof.package_photo_url && d.proof.proof_photo_id && (
                  <p className="mb-2 text-[11px] text-ink-soft">Compare the sender's packed photo with the delivered photo to settle any dispute.</p>
                )}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <ProofUrlImage url={d.proof.package_photo_url} label="Packed by sender" />
                  <ProofImage rideId={d.proof.ride_id} photoId={d.proof.pickup_photo_id} label="Pickup" />
                  <ProofImage rideId={d.proof.ride_id} photoId={d.proof.proof_photo_id} label="Delivered" />
                  <ProofImage rideId={d.proof.ride_id} photoId={d.proof.proof_signature_id} label="Signature" />
                </div>
              </div>
            )}

            {/* Receiver feedback */}
            {d.kind === "goods" && d.receiver_feedback && (
              <div className="kt-card p-4" data-testid="order-detail-receiver-feedback">
                <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted"><Star className="h-3.5 w-3.5" /> Receiver feedback</p>
                <p className="flex items-center gap-1 text-amber-600">{"★".repeat(d.receiver_feedback.rating)}<span className="text-ink-muted">{"★".repeat(5 - d.receiver_feedback.rating)}</span><span className="ml-2 text-xs text-ink-muted">({d.receiver_feedback.rating}/5)</span></p>
                {d.receiver_feedback.review && <p className="mt-1 text-sm text-ink-soft">“{d.receiver_feedback.review}”</p>}
              </div>
            )}

            {/* Edit history — what the customer changed on this delivery */}
            {Array.isArray(d.edit_log) && d.edit_log.length > 0 && (
              <div className="kt-card p-4" data-testid="order-detail-edit-log">
                <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted"><History className="h-3.5 w-3.5" /> Edit history</p>
                <ol className="space-y-3">
                  {d.edit_log.map((e, i) => (
                    <li key={i} className="border-l-2 border-surface-200 pl-3" data-testid={`order-edit-entry-${i}`}>
                      <p className="text-xs text-ink-muted">{e.at ? new Date(e.at).toLocaleString() : ""} · by {e.by}</p>
                      <ul className="mt-1 space-y-0.5 text-sm text-ink-soft">
                        {(e.changes || []).map((c, j) => (
                          <li key={j}><span className="font-medium text-ink">{c.field}:</span> {String(c.from)} → {String(c.to)}</li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
